import React, { useState, useRef, useEffect } from 'react';
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiX,
  FiUser,
  FiMail,
  FiPhone,
  FiUsers,
  FiMessageSquare,
  FiCheckCircle,
  FiAlertCircle,
  FiChevronRight,
  FiPlus,
  FiMinus,
  FiStar,
  FiLoader,
  FiInfo,
  FiTag,
} from 'react-icons/fi';
import './HoneymoonDetailsPage.css';
import api from '../../../Api';

const TOKEN_KEY = 'accessTok';
const ADMIN_DASHBOARD_URL =
  'https://admintest.jayrajgroup.org/admin/honeymoon-bookings';

export default function HoneymoonBookingSection({ packageData, destinationName, plans }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    guests: 2,
    wantVeg: true,
    wantNonVeg: true,
    specialNote: '',
    couponCode: '',
  });
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPlanDetail, setSelectedPlanDetail] = useState(null);
  const [planDetailLoading, setPlanDetailLoading] = useState(false);
  const [addons, setAddons] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);
  const today = new Date();
  const minimumDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // --- Fetch plan details (to know add-ons) when a plan is selected ---
  useEffect(() => {
    if (!selectedPlanId) {
      setSelectedPlanDetail(null);
      setPlanDetailLoading(false);
      return;
    }
    let isCurrentSelection = true;
    setSelectedPlanDetail(null);
    setAddons([]);
    const fetchPlanDetail = async () => {
      setPlanDetailLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const response = await api.get(`/honeymoon/plans/${selectedPlanId}`, {
          headers: { 'X-Admin-Token': `${token}` },
        });
        if (!isCurrentSelection) return;
        if (response.data.status) {
          setSelectedPlanDetail(response.data.data);
        } else {
          setError(response.data.message || 'Failed to load plan details');
        }
      } catch (err) {
        if (!isCurrentSelection) return;
        console.error('Plan detail fetch error:', err);
        if (err.response?.status === 401) {
          alert('Session expired. Try again from admin.');
          return;
        }
        setError('Could not load plan details. Please try again.');
      } finally {
        if (isCurrentSelection) setPlanDetailLoading(false);
      }
    };
    fetchPlanDetail();
    return () => {
      isCurrentSelection = false;
    };
  }, [selectedPlanId]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isModalOpen]);

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!isSubmitting && modalRef.current && !modalRef.current.contains(e.target)) {
        setIsModalOpen(false);
      }
    };
    if (isModalOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isModalOpen, isSubmitting]);

  // --- Helpers ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const getAvailableAddons = () => {
    if (!selectedPlanDetail || !selectedPlanDetail.itinerary) return [];
    const allAddons = [];
    (selectedPlanDetail.itinerary.days || []).forEach((day) => {
      if (day.addons) {
        day.addons.forEach((a) => {
          if (!allAddons.find((ex) => ex.id === a.id)) {
            allAddons.push({ ...a, quantity: 0 });
          }
        });
      }
    });
    return allAddons;
  };

  const handleAddonQuantity = (addonId, change) => {
    setAddons((prev) => {
      const existing = prev.find((a) => a.id === addonId);
      if (existing) {
        const newQty = Math.max(0, existing.quantity + change);
        if (newQty === 0) return prev.filter((a) => a.id !== addonId);
        return prev.map((a) => (a.id === addonId ? { ...a, quantity: newQty } : a));
      }
      if (change > 0) return [...prev, { id: addonId, quantity: 1 }];
      return prev;
    });
  };

  // --- Navigation handlers ---
  const handleNext = () => {
    if (step === 1) {
      if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
        setError('Please fill in all required contact fields.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        setError('Please enter a valid email address.');
        return;
      }
      if (!formData.date || formData.date < minimumDate) {
        setError('Please select today or a future tour start date.');
        return;
      }
      const guests = Number(formData.guests);
      if (!Number.isInteger(guests) || guests < 2 || guests > 20) {
        setError('Please enter a guest count between 2 and 20.');
        return;
      }
      setError(null);
    }
    if (step === 2) {
      if (!selectedPlanId) {
        setError('Please select a plan.');
        return;
      }
      if (planDetailLoading || !selectedPlanDetail) {
        setError('Please wait for the selected plan details to finish loading.');
        return;
      }
      setError(null);
    }
    if (step < 3) setStep(step + 1);
  };

  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
    setError(null);
  };

  // --- Open Modal ---
  const handleBookNow = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      alert('No admin session found. Please sign in to the admin panel and try again.');
      return;
    }
    setIsModalOpen(true);
  };

  // --- Submit booking (ONLY runs when step === 3 & Confirm button is clicked) ---
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (step !== 3) {
      return;
    }

    if (isSubmitting) return;

    // Final safety validation check
    if (!selectedPlanId) {
      setError('Please select a plan.');
      return;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setError('Your admin session has expired. Please sign in again.');
      return;
    }
    setIsSubmitting(true);
    setError(null);

    const payload = {
      plan_id: selectedPlanId,
      pax_count: Number(formData.guests),
      tour_start_date: formData.date,
      contact_name: formData.name.trim(),
      contact_mobile: formData.phone.trim(),
      contact_email: formData.email.trim(),
      want_veg_food: formData.wantVeg,
      want_nonveg_food: formData.wantNonVeg,
      booking_source: 'admin',
      addons: addons.map((a) => ({ id: a.id, quantity: a.quantity })),
      special_note: formData.specialNote.trim(),
      geo: {
        lat: 22.5726,
        lng: 88.3639,
        accuracy_meters: 12.5,
        source: 'gps',
      },
    };

    if (formData.couponCode && formData.couponCode.trim() !== '') {
      payload.coupon_code = formData.couponCode.trim();
    }

    try {
      const response = await api.post('/admin/honeymoon/bookings', payload, {
        headers: { 'X-Admin-Token': `${token}` },
      });

      if (!response.data.status) {
        throw new Error(response.data.message || 'Booking failed.');
      }

      setBookingResult(response.data.data);
      window.location.href = ADMIN_DASHBOARD_URL;
    } catch (err) {
      if (err.response && err.response.status === 401) {
        alert('Your admin token is invalid or expired. Try again from admin.');
      } else {
        console.error('Booking submission error:', err);
        setError(
          err.response?.data?.message || err.message || 'An error occurred. Please try again.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToAdminDashboard = () => {
    window.location.href = ADMIN_DASHBOARD_URL;
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      name: '',
      email: '',
      phone: '',
      date: '',
      guests: 2,
      wantVeg: true,
      wantNonVeg: true,
      specialNote: '',
      couponCode: '',
    });
    setSelectedPlanId(null);
    setSelectedPlanDetail(null);
    setAddons([]);
    setBookingResult(null);
    setError(null);
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);
  const availableAddons = getAvailableAddons();

  const buildReviewSummary = () => {
    const selectedAddonsList = addons
      .map((a) => {
        const addon = availableAddons.find((x) => x.id === a.id);
        return addon
          ? { name: addon.addon_name, quantity: a.quantity, price: addon.price_per_person }
          : null;
      })
      .filter(Boolean);

    const foodPrefs = [
      formData.wantVeg ? 'Veg' : null,
      formData.wantNonVeg ? 'Non-Veg' : null,
    ]
      .filter(Boolean)
      .join(' & ') || 'Not selected';

    return { selectedAddonsList, foodPrefs };
  };

  const { selectedAddonsList, foodPrefs } = buildReviewSummary();

  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && step !== 3) {
      if (e.target.tagName === 'TEXTAREA') return;
      e.preventDefault();
    }
  };

  const renderPlanOptions = () => {
    return (
      <div className="pkgd-plan-selector-grid">
        {plans.map((plan) => {
          const price = plan.couple_price || plan.price_per_person * (plan.pax_count || 2);
          const isSelected = selectedPlanId === plan.id;
          const isLoading = planDetailLoading && isSelected;
          return (
            <div
              key={plan.id}
              className={`pkgd-plan-selector-card ${isSelected ? 'pkgd-selected' : ''}`}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              onClick={() => setSelectedPlanId(plan.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelectedPlanId(plan.id);
                }
              }}
            >
              <div className="pkgd-plan-selector-header">
                <span className="pkgd-plan-tier-badge">{plan.tier_label || plan.tier}</span>
                <span className="pkgd-plan-selector-price">{formatPrice(price)}</span>
              </div>
              <h4 className="pkgd-plan-selector-name">{plan.plan_name}</h4>
              <p className="pkgd-plan-selector-pax">{plan.pax_count} guests</p>
              <div className="pkgd-plan-selector-badges">
                {plan.has_veg_food && <span className="pkgd-tag pkgd-tag-veg">Veg</span>}
                {plan.has_nonveg_food && <span className="pkgd-tag pkgd-tag-nonveg">Non‑Veg</span>}
              </div>
              {isLoading && (
                <div className="pkgd-plan-selector-loading">
                  <FiLoader className="pkgd-spinner" />
                </div>
              )}
              {isSelected && !isLoading && (
                <div className="pkgd-plan-selector-check">
                  <FiCheckCircle />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSuccessScreen = () => (
    <div className="pkgd-booking-summary-full">
      <FiCheckCircle className="pkgd-success-icon" />
      <h3>Booking Created!</h3>
      <p className="pkgd-booking-number">
        Booking #: {bookingResult?.booking_code || bookingResult?.booking_id || '—'}
      </p>
      <button
        type="button"
        className="pkgd-btn-submit"
        onClick={goToAdminDashboard}
      >
        Go to Admin Dashboard <FiChevronRight />
      </button>
      <button
        type="button"
        className="pkgd-btn-close"
        onClick={() => { setIsModalOpen(false); resetForm(); }}
      >
        Close
      </button>
    </div>
  );

  return (
    <>
      {/* Sidebar */}
      <div className="pkgd-booking-sidebar">
        <div className="pkgd-booking-card">
          <h3>Book This Honeymoon</h3>
          <div className="pkgd-booking-quick-info">
            <div className="pkgd-booking-info-item">
              <FiCalendar />
              <span>{packageData?.duration_days} Days</span>
            </div>
            <div className="pkgd-booking-info-item">
              <FiClock />
              <span>{packageData?.duration_nights} Nights</span>
            </div>
            <div className="pkgd-booking-info-item">
              <FiMapPin />
              <span>{destinationName}</span>
            </div>
          </div>
          <button className="pkgd-btn-book" onClick={handleBookNow}>
            Book Now <FiChevronRight />
          </button>
          <p className="pkgd-booking-note">
            <FiInfo />
            Manual admin booking - no online payment required
          </p>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="pkgd-modal-overlay">
          <div className="pkgd-modal-container" ref={modalRef}>
            <button
              className="pkgd-modal-close"
              disabled={isSubmitting}
              onClick={() => { setIsModalOpen(false); resetForm(); }}
            >
              <FiX />
            </button>

            <div className="pkgd-modal-header">
              <h2>Book Your Honeymoon</h2>
              <p>
                <strong>{packageData?.name}</strong> in {destinationName}
              </p>
            </div>

            {bookingResult ? (
              renderSuccessScreen()
            ) : (
              <>
                <div className="pkgd-modal-steps">
                  <div className={`pkgd-step-indicator ${step >= 1 ? 'pkgd-active' : ''}`}>
                    <span>1</span>
                    <label>Contact</label>
                  </div>
                  <div className={`pkgd-step-indicator ${step >= 2 ? 'pkgd-active' : ''}`}>
                    <span>2</span>
                    <label>Select Plan</label>
                  </div>
                  <div className={`pkgd-step-indicator ${step >= 3 ? 'pkgd-active' : ''}`}>
                    <span>3</span>
                    <label>Review</label>
                  </div>
                </div>

                {error && (
                  <div className="pkgd-error-message">
                    <FiAlertCircle />
                    <span>{error}</span>
                  </div>
                )}

                <form
                  onSubmit={(e) => e.preventDefault()}
                  onKeyDown={handleFormKeyDown}
                  className="pkgd-modal-form"
                >
                  {/* Step 1: Contact Details */}
                  {step === 1 && (
                    <div className="pkgd-form-step">
                      <div className="pkgd-form-group">
                        <label><FiUser /> Full Name <span>*</span></label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" required />
                      </div>
                      <div className="pkgd-form-group">
                        <label><FiMail /> Email <span>*</span></label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" required />
                      </div>
                      <div className="pkgd-form-group">
                        <label><FiPhone /> Phone <span>*</span></label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 98765 43210" required />
                      </div>
                      <div className="pkgd-form-group">
                        <label><FiCalendar /> Tour Start Date <span>*</span></label>
                        <input type="date" name="date" value={formData.date} onChange={handleChange} min={minimumDate} required />
                      </div>
                      <div className="pkgd-form-group">
                        <label><FiUsers /> Number of Guests</label>
                        <input type="number" name="guests" value={formData.guests} onChange={handleChange} min="2" max="20" required />
                      </div>
                    </div>
                  )}

                  {/* Step 2: Plan Selection, Add-ons, Coupon */}
                  {step === 2 && (
                    <div className="pkgd-form-step">
                      <div className="pkgd-form-group">
                        <label><FiStar /> Select Plan <span>*</span></label>
                        {renderPlanOptions()}
                      </div>

                      {selectedPlanId && selectedPlanDetail && (
                        <>
                          <div className="pkgd-form-group">
                            <label>Food Preferences</label>
                            <div className="pkgd-checkbox-group">
                              <label>
                                <input
                                  type="checkbox"
                                  name="wantVeg"
                                  checked={formData.wantVeg}
                                  onChange={handleChange}
                                />
                                Veg
                              </label>
                              <label>
                                <input
                                  type="checkbox"
                                  name="wantNonVeg"
                                  checked={formData.wantNonVeg}
                                  onChange={handleChange}
                                />
                                Non‑Veg
                              </label>
                            </div>
                          </div>

                          {availableAddons.length > 0 && (
                            <div className="pkgd-form-group">
                              <label>Add‑ons</label>
                              {availableAddons.map((addon) => {
                                const selected = addons.find((a) => a.id === addon.id);
                                const qty = selected ? selected.quantity : 0;
                                return (
                                  <div key={addon.id} className="pkgd-addon-item">
                                    <span>{addon.addon_name} (₹{addon.price_per_person}/person)</span>
                                    <div className="pkgd-quantity-control">
                                      <button type="button" onClick={() => handleAddonQuantity(addon.id, -1)}>
                                        <FiMinus />
                                      </button>
                                      <span>{qty}</span>
                                      <button type="button" onClick={() => handleAddonQuantity(addon.id, 1)}>
                                        <FiPlus />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div className="pkgd-form-group">
                            <label><FiTag /> Coupon Code (optional)</label>
                            <div className="pkgd-coupon-input">
                              <input
                                type="text"
                                name="couponCode"
                                value={formData.couponCode}
                                onChange={handleChange}
                                placeholder="Enter coupon code"
                              />
                            </div>
                          </div>
                        </>
                      )}

                      <div className="pkgd-form-group">
                        <label><FiMessageSquare /> Special Note</label>
                        <textarea
                          name="specialNote"
                          value={formData.specialNote}
                          onChange={handleChange}
                          placeholder="Any special requests or notes..."
                          rows="3"
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Review */}
                  {step === 3 && (
                    <div className="pkgd-form-step pkgd-confirmation-step">
                      <div className="pkgd-confirmation-box">
                        <h4>Review Your Booking</h4>
                        <div className="pkgd-confirmation-details">
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Package</span>
                            <span className="pkgd-conf-value">{packageData?.name}</span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Destination</span>
                            <span className="pkgd-conf-value">{destinationName}</span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Duration</span>
                            <span className="pkgd-conf-value">
                              {packageData?.duration_days} Days, {packageData?.duration_nights} Nights
                            </span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Name</span>
                            <span className="pkgd-conf-value">{formData.name}</span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Email</span>
                            <span className="pkgd-conf-value">{formData.email}</span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Phone</span>
                            <span className="pkgd-conf-value">{formData.phone}</span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Start Date</span>
                            <span className="pkgd-conf-value">
                              {new Date(formData.date).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Guests</span>
                            <span className="pkgd-conf-value">{formData.guests}</span>
                          </div>
                          {selectedPlan && (
                            <>
                              <div className="pkgd-conf-item">
                                <span className="pkgd-conf-label">Plan</span>
                                <span className="pkgd-conf-value">{selectedPlan.plan_name}</span>
                              </div>
                              <div className="pkgd-conf-item">
                                <span className="pkgd-conf-label">Food</span>
                                <span className="pkgd-conf-value">{foodPrefs}</span>
                              </div>
                              {selectedAddonsList.length > 0 && (
                                <div className="pkgd-conf-item">
                                  <span className="pkgd-conf-label">Add‑ons</span>
                                  <span className="pkgd-conf-value">
                                    {selectedAddonsList
                                      .map((a) => `${a.name}×${a.quantity}`)
                                      .join(', ')}
                                  </span>
                                </div>
                              )}
                              {formData.couponCode && (
                                <div className="pkgd-conf-item">
                                  <span className="pkgd-conf-label">Coupon</span>
                                  <span className="pkgd-conf-value">{formData.couponCode}</span>
                                </div>
                              )}
                            </>
                          )}
                          {formData.specialNote && (
                            <div className="pkgd-conf-item">
                              <span className="pkgd-conf-label">Note</span>
                              <span className="pkgd-conf-value">{formData.specialNote}</span>
                            </div>
                          )}
                        </div>
                        <div className="pkgd-confirmation-note">
                          <FiAlertCircle />
                          <span>This creates an admin booking. No payment is collected here.</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pkgd-modal-actions">
                    {step > 1 && (
                      <button type="button" className="pkgd-btn-prev" onClick={handlePrev}>
                        Back
                      </button>
                    )}
                    {step < 3 ? (
                      <button type="button" className="pkgd-btn-next" onClick={handleNext}>
                        Next Step <FiChevronRight />
                      </button>
                    ) : (
                      <button type="button" className="pkgd-btn-submit" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <span className="pkgd-spinner"></span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <FiCheckCircle />
                            Confirm & Create Booking
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}